import type { PersonalSource, PersonalSourcesResponse } from "@/api/client/personal-sources"

/**
 * Pure helpers for personal search in Explorer (slice 6H). Nothing here talks to the
 * network or decides anything the server re-checks: the server mints the grant, owns the
 * toggle and marks the messages. These only describe what to show.
 */

/** The closed set of citation source types the server labels personal results with. */
export type PersonalCitationSourceType =
  | "personal_onedrive_file"
  | "personal_mail_message"
  | "personal_mail_attachment"
  | "personal_chat_day"
  | "teams_chat_live"

/** Virtual library id the server gives the personal leg. Citations to it are never opened. */
export const PERSONAL_LIBRARY_ID = "__personal__"

/**
 * True when a citation names the virtual personal library. Those citations scroll to the
 * reference list and never build a preview or download URL.
 */
export function isPersonalLibraryCitation(citation: string): boolean {
  const head = citation.split("|")[0] ?? ""
  let source = (head.split("#")[0] ?? "").trim()
  if (source.startsWith("library:")) {
    const withoutPrefix = source.slice("library:".length)
    if (withoutPrefix.includes(":")) source = withoutPrefix
  }
  const separator = source.indexOf(":")
  if (separator <= 0) return false
  return source.slice(0, separator) === PERSONAL_LIBRARY_ID
}

/** i18n key and English fallback per source type: "From your mail" and so on. */
export const PERSONAL_CITATION_LABELS: Record<
  PersonalCitationSourceType,
  { key: string; defaultValue: string }
> = {
  personal_onedrive_file: {
    key: "personalSources.citation.onedrive",
    defaultValue: "From your OneDrive",
  },
  personal_mail_message: { key: "personalSources.citation.mail", defaultValue: "From your mail" },
  personal_mail_attachment: {
    key: "personalSources.citation.mailAttachment",
    defaultValue: "Attachment in your mail",
  },
  personal_chat_day: { key: "personalSources.citation.chat", defaultValue: "From your chat" },
  teams_chat_live: {
    key: "personalSources.citation.chatToday",
    defaultValue: "From your chat (today)",
  },
}

export function isPersonalCitationSourceType(value: unknown): value is PersonalCitationSourceType {
  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(PERSONAL_CITATION_LABELS, value)
  )
}

/** The personal source type a document's `extra_data` carries, or null for every other document. */
export function personalCitationSourceType(extraData: unknown): PersonalCitationSourceType | null {
  if (typeof extraData !== "object" || extraData === null) return null
  const value = (extraData as Record<string, unknown>).filth_source_type
  return isPersonalCitationSourceType(value) ? value : null
}

/** Which of the user's sources a personal citation type belongs to (for the icon). */
export function personalSourceForCitationType(type: PersonalCitationSourceType): PersonalSource {
  switch (type) {
    case "personal_onedrive_file":
      return "onedrive"
    case "personal_mail_message":
    case "personal_mail_attachment":
      return "mail"
    default:
      return "chats"
  }
}

// ------------------------------------------------------------------ the toggle

/** The slice of a session the toggle needs (all of it comes from GET /api/get-session). */
export type PersonalSearchSession = {
  userId?: string
  currentUserRole?: string
  hostedAgentId?: string | null
  participants?: ReadonlyArray<{ oid?: string; type?: string }> | null
  personalSearch?: { enabled?: boolean } | null
  personalTainted?: boolean
}

export type PersonalSearchSessionState = {
  /** What the server has stored for this chat. Absent means on (PS-5). */
  enabled: boolean
  /** Personal search is forced off here: someone else can see this chat. */
  locked: boolean
  /** Personal content is part of this chat, so it cannot be shared. */
  tainted: boolean
}

export function personalSearchSessionState(
  session: PersonalSearchSession | null | undefined,
  currentUserOid: string | null | undefined
): PersonalSearchSessionState {
  if (!session) return { enabled: true, locked: false, tainted: false }
  const ownerOid = session.userId
  const sharedWithOthers = (session.participants ?? []).some(
    (participant) => participant.type === "group" || participant.oid !== ownerOid
  )
  const notOwner =
    session.currentUserRole === "reader" ||
    (typeof ownerOid === "string" &&
      typeof currentUserOid === "string" &&
      currentUserOid.length > 0 &&
      ownerOid !== currentUserOid)
  return {
    enabled: session.personalSearch?.enabled !== false,
    locked: sharedWithOthers || notOwner || Boolean(session.hostedAgentId),
    tainted: session.personalTainted === true,
  }
}

/** Sources the grant will search: eligible, consented, and acknowledged by FILTH. */
export function searchablePersonalSources(
  response: PersonalSourcesResponse | null | undefined
): PersonalSourcesResponse["sources"] {
  if (!response?.available) return []
  return response.sources.filter(
    (view) => view.eligible && view.consent_state === "active" && view.filth_ack === "acked"
  )
}

/** The row is shown only when the deployment wires personal sources and the user has one on. */
export function personalSearchAvailable(
  response: PersonalSourcesResponse | null | undefined
): boolean {
  return searchablePersonalSources(response).length > 0
}

/**
 * The per-turn request field. The server computes effective = session AND request, so an
 * absent field means "on". Only an explicit off is ever sent, and only when the feature exists.
 */
export function personalSearchRequestFlag(available: boolean, enabled: boolean): false | undefined {
  return available && !enabled ? false : undefined
}

// ---------------------------------------------------------- marked messages

/** True when the saved or streamed message carries the server's `personal_sources` marker. */
export function isPersonalSourcesContext(context: unknown): boolean {
  if (typeof context !== "object" || context === null) return false
  const marker = (context as Record<string, unknown>).personal_sources
  return typeof marker === "object" && marker !== null
}

/**
 * Hrefs the markdown renderer handles itself (citations, sandbox files, artifacts) and so are
 * not external navigation. Everything else with a scheme or a protocol-relative form is.
 */
const INTERNAL_SCHEMES = ["cite:", "sandbox:", "artifact:"]

export function isExternalUrl(value: string | null | undefined): boolean {
  if (typeof value !== "string") return false
  const trimmed = value.trim()
  if (trimmed === "") return false
  const lower = trimmed.toLowerCase()
  if (INTERNAL_SCHEMES.some((scheme) => lower.startsWith(scheme))) return false
  // Same-page fragments and root/relative app paths stay clickable; anything that can leave
  // the app does not: http(s), protocol-relative, data/blob/javascript/mailto/tel and the rest.
  if (lower.startsWith("#")) return false
  // WHATWG treats a leading "\" or "/\" as scheme-relative, the same class as "//":
  // new URL("\\evil.example/path", "https://app.example/chat") is https://evil.example/path.
  if (lower.startsWith("\\") || lower.startsWith("/\\")) return true
  if (lower.startsWith("//")) return true
  if (/^[a-z][a-z0-9+.-]*:/.test(lower)) return true
  return false
}

/**
 * What to show instead of a link in a marked message: the link text and the full address as plain
 * text, so the user can see where it points and decide for themselves. Never a clickable element.
 */
export function neutralizedLinkText(label: string, href: string): string {
  const text = label.trim()
  if (text === "" || text === href) return href
  return `${text} (${href})`
}
